/**
 *
 * UsherHistory
 *
 */
import React, { memo, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import { Col, Empty, Row, Table, Tabs, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import moment from 'moment';

import { useTranslation } from 'react-i18next';

import { Loading } from 'app/components/Loading';
import { GET_HISTORY } from 'app/graphql/queries';

message.config({ top: 100 }); // set a lower position

interface Props {}

export const UsherHistory = memo((props: Props) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const { usherUserID } = useParams<{ usherUserID: string }>();

  const [tabKey, setTabKey] = useState('2');

  const {
    data: historyData,
    loading: historyLoading,
    error: historyError,
  } = useQuery(GET_HISTORY, {
    variables: { userID: usherUserID },
  });

  useEffect(() => {
    message.destroy(); // clear previous

    if (!historyLoading && historyError) {
      message.error(historyError.message);
    }
  }, [tabKey, historyError, historyLoading]);

  const onChange = key => {
    message.destroy(); // Clear any existing messages
    setTabKey(key);
  };

  const formatCompletedValue = node => {
    if (node.initiative) {
      return node.totalDays
        ? `${node.totalDays} ${t('historyPage.daysUnit')}`
        : 'N/A';
    }

    return node.totalHours
      ? `${node.totalHours} ${t('historyPage.hoursUnit')}`
      : 'N/A';
  };

  const getCompletedValueColumn = (tab: string) => {
    if (tab === '1') {
      return {
        title: t('historyPage.completedHours'),
        width: '5%',
        align: 'center' as const,
        render: (data, row_data) => row_data.node.totalHours || 'N/A',
      };
    }

    if (tab === '2') {
      return {
        title: t('historyPage.completedDays'),
        width: '5%',
        align: 'center' as const,
        render: (data, row_data) => row_data.node.totalDays || 'N/A',
      };
    }

    return {
      title: t('historyPage.completed'),
      width: '5%',
      align: 'center' as const,
      render: (data, row_data) => formatCompletedValue(row_data.node),
    };
  };

  const columns: ColumnsType<any> = [
    {
      title: t('historyPage.programName'),
      width: '5%',
      align: 'center',
      render: (data, row_data) => {
        return row_data.node[
          currentLanguage === 'en' ? 'programTitle' : 'programTitleAr'
        ];
      },
    },
    {
      title: t('historyPage.programDates'),
      width: '5%',
      align: 'center',
      render: (data, row_data) => {
        if (row_data.node.program)
          return (
            moment(row_data.node.program.startingDate).format('D/M/YYYY') +
            t('historyPage.to') +
            moment(row_data.node.program.endingDate).format('D/M/YYYY')
          );
        if (row_data.node.activity)
          return moment(row_data.node.activity.date).format('D/M/YYYY');
        if (row_data.node.initiative)
          return (
            moment(row_data.node.initiative.startingDate).format('D/M/YYYY') +
            t('historyPage.to') +
            moment(row_data.node.initiative.endingDate).format('D/M/YYYY')
          );
        return 'N/A';
      },
    },
    {
      title: t('historyPage.status'),
      width: '5%',
      align: 'center',
      render: (data, row_data) => {
        return row_data.node.status;
      },
    },
  ];

  const renderTabContent = (
    isActive: boolean,
    loading: boolean,
    data: any[] | undefined,
  ) => {
    if (!isActive) return null;
    if (loading) return <Loading />;
    if (!data || (data && data.length === 0))
      return <Empty description={t('noData')} />;
    return (
      <Table
        className="my-4"
        dataSource={data}
        columns={[...columns, getCompletedValueColumn(tabKey)]}
        pagination={false}
        rowKey={record => record.node.id}
      />
    );
  };

  const tabConfigs = [
    {
      key: '1',
      label: t('historyPage.LOYACPrograms'),
      data: historyData?.registrations?.edges,
    },
    {
      key: '2',
      label: t('historyPage.LSVInitiatives'),
      data: historyData?.LSVRegistrations?.edges,
    },
    {
      key: '3',
      label: t('historyPage.all'),
      data: [
        ...(historyData?.registrations?.edges || []),
        ...(historyData?.LSVRegistrations?.edges || []),
      ],
    },
  ];

  const items = tabConfigs.map(tab => ({
    key: tab.key,
    label: tab.label,
    children: renderTabContent(tabKey === tab.key, historyLoading, tab.data),
  }));

  return historyLoading ? (
    <Loading />
  ) : (
    <Row justify="center" style={{ textAlign: 'center' }}>
      <Col md={18} xs={24}>
        <Tabs defaultActiveKey="2" items={items} onChange={onChange} />
      </Col>
    </Row>
  );
});
